/**
 * Which directives a game can ANSWER (D229, E15): what a pack must declare
 * for each runtime action to have anything to act on, and the question HELP
 * asks of every verb before listing it.
 *
 * @remarks
 * D43 derived the HELP listing from the compiled vocabulary so it could not
 * drift from what the grammar accepts. The play-test found the next lie: the
 * grammar accepts `equip`, `unequip`, `open`, `close` and `use` in every
 * game, because the base vocabulary declares them, and a game with nothing
 * to wear, no door and no interaction listed all five — directives that
 * exist in the vocabulary and do nothing in this world. A listing is help
 * only when every line on it can be answered.
 *
 * So each action states the AFFORDANCE it needs — the declaration somewhere
 * in the pack without which the action can only refuse — and a verb is
 * listed when any action it produces has one. Actions with no entry here
 * always have something to act on: the room is always there to look at,
 * the hands to count, the world to wait in. The authored verbs (D196) are
 * judged by the table that answers them, `verbInteractions`; the chrome
 * directives (`again`, `restart`, `save`, `load`, `never mind`) are the
 * terminal's and always answer — D179 withholds `save` / `load` where they
 * would only say there is nothing to do, and that rule stays where it is.
 *
 * The table is a second statement of what the handlers do, exactly as
 * `INTENTS_PRODUCING_ACTION` is of what the roster does; the pin that keeps
 * it honest is the suite that plays each withheld verb against a pack
 * lacking its affordance and reads the refusal.
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition } from '@orpheus/content-schema';

import type { RuntimeAction } from './runtime-action.js';

import { ENGINE_CLAIMED_INTENT_TOKENS, INTENTS_PRODUCING_ACTION } from './intent-action-map.js';

/** Whether anything in the pack can be acted on this way. */
type Affordance = (contentPack: ContentPackDefinition) => boolean;

/** Something the player can carry: a portable thing, or one they start with. */
const somethingToCarry: Affordance = (contentPack) =>
	contentPack.items.some((item) => item.isPortable) || contentPack.manifest.initialInventory.length > 0;

/** A door somewhere — a thing declaring `openable`. */
const somethingWithADoor: Affordance = (contentPack) => contentPack.items.some((item) => item.openable !== undefined);

/**
 * Someone, or something, with an answer — `askNarratives` or (D231) `topics` on a thing or a room, or (D232) an actor
 * with any of those or a greeting, or (D235) an actor whose `talk` opens a prompt.
 */
const somethingToAsk: Affordance = (contentPack) =>
	contentPack.items.some((item) => item.askNarratives !== undefined || item.topics !== undefined) ||
	contentPack.rooms.some((room) => room.askNarratives !== undefined || room.topics !== undefined) ||
	contentPack.actors.some(
		(actor) =>
			actor.askNarratives !== undefined ||
			actor.greetingNarratives !== undefined ||
			actor.opensWith !== undefined ||
			actor.topics !== undefined,
	);

/** D235: somebody who takes gifts — an actor declaring `giveInteractions`. Without one, `give` and `show` can only refuse. */
const somebodyWhoTakesGifts: Affordance = (contentPack) => contentPack.actors.some((actor) => actor.giveInteractions.length > 0);

/**
 * What each action needs the pack to declare before it can do anything but
 * refuse. An action absent here always has something to act on.
 */
const AFFORDANCE_BY_ACTION: Partial<Record<RuntimeAction['type'], Affordance>> = {
	ask: somethingToAsk,
	// D234: a foe — an actor declaring `combat`. Without one the verb can only refuse, on bodies and things alike.
	attack: (contentPack) => contentPack.actors.some((actor) => actor.combat !== undefined),
	close_container: somethingWithADoor,
	drop: somethingToCarry,
	// D143: the wear family needs a thing declaring `equippable`.
	equip: (contentPack) => contentPack.items.some((item) => item.equippable !== undefined),
	give: somebodyWhoTakesGifts,
	open_container: somethingWithADoor,
	show: somebodyWhoTakesGifts,
	switch_off: (contentPack) => contentPack.items.some((item) => item.switchable !== undefined),
	switch_on: (contentPack) => contentPack.items.some((item) => item.switchable !== undefined),
	take: (contentPack) => contentPack.items.some((item) => item.isPortable),
	unequip: (contentPack) => contentPack.items.some((item) => item.equippable !== undefined),
	use: (contentPack) => contentPack.items.some((item) => item.useInteractions.length > 0),
};

/**
 * Can this game answer the directive — is there anything in it for the verb
 * to act on?
 *
 * @param canonicalToken - The verb's canonical token
 * @param contentPack - The game
 * @param movementIntentToken - The vocabulary's movement intent, which the roster lists under no typed verb (`go: []`) and which a world always answers — somewhere to go, or the one room to be told so in
 * @returns Whether HELP may list it
 */
export const gameAnswersDirective = (canonicalToken: string, contentPack: ContentPackDefinition, movementIntentToken: string): boolean => {
	if (canonicalToken === movementIntentToken) {
		return true;
	}

	// D196: a verb the engine does not claim is the pack's own, answered by
	// the things — and (D251) the bodies — that declare arms for it, and by
	// nothing else.
	if (!ENGINE_CLAIMED_INTENT_TOKENS.includes(canonicalToken)) {
		return [...contentPack.items, ...contentPack.actors].some((owner) => canonicalToken in (owner.verbInteractions ?? {}));
	}

	const actionTypes = (Object.keys(INTENTS_PRODUCING_ACTION) as readonly RuntimeAction['type'][]).filter((actionType) =>
		(INTENTS_PRODUCING_ACTION[actionType] as readonly string[]).includes(canonicalToken),
	);

	// A chrome directive produces no action and always answers; a verb that
	// produces several is answered when any one of them is.
	return actionTypes.length === 0 || actionTypes.some((actionType) => (AFFORDANCE_BY_ACTION[actionType] ?? (() => true))(contentPack));
};
